import { ImpactActions } from '../actions/impacts';
import { ImpactConfigActions } from '../actions/config';
import { ImpactFindAndFixActions } from '../actions/findandfix';
import {
  ConfigById,
  GenerateOpportunitiesProgress,
  ImpactTypes,
  MutedOppConfigsBySignalKey,
  OpportunitiesLoadingStatus,
  OpportunitiesWithID,
  SignalsWithID,
} from '../types';
import { combineReducers } from '@fullstory/redux-util/utils/combineReducers';
import { fetchAction } from '@fullstory/redux-util/utils/fetchStatus';
import {
  ComputeFunnelMetricsRequest,
  ComputeFunnelMetricsResponse,
} from '@fullstory/proto/fs/services/alexa';
import {
  CustomQueryProto,
  OpportunitySnapshot,
  SnapshotShareLinkResponse,
  MutedOpportunityConfig,
} from '@fullstory/proto/fs/services/webber/internal/impacts';
import { UID } from '@fullstory/util/uid';
import {
  createErrorReducer,
  createLoadingReducer,
  createRequestReducer,
} from '@fullstory/redux-util/middleware/request/reducers';
import { DispatchedAction, RequestState } from '@fullstory/redux-util/types';
import { currentConfigId, currentConfig } from './currentConfig';
import { pendingConfig } from './pendingConfig';
import { clone } from 'lodash';
import { dimensions } from './dimensions';
import { revenueAttribution } from './revenueAttribution';
import { ActionType } from '@fullstory/redux-util/utils';
import { int64 } from '@fullstory/proto/google/protobuf';
import { conversionsSpec } from './spec';

function detailPaginationStep(state: number = 0, action: ImpactActions): number {
  switch (action.type) {
    case ImpactTypes.SetOpportunityDetail:
      return 0;
    case ImpactTypes.SetDetailFocusedStep:
      return Math.max(action.page, 0);
  }
  return state;
}

type UserSentenceState = {
  converted: ReturnType<typeof sentenceConverted>;
  hadExperience: ReturnType<typeof sentenceHadExperience>;
};

// holds state for matching sessions within the "Watch Sessions" tab of I&I.
const userSentence = combineReducers<UserSentenceState>({
  converted: sentenceConverted,
  hadExperience: sentenceHadExperience,
});

function sentenceConverted(state: boolean = false, action: ImpactActions): boolean {
  switch (action.type) {
    // if a new detail view is selected then reset the filter to the default state
    case ImpactTypes.SetOpportunityDetail:
      return false;
    case ImpactTypes.SetUserSentenceConverted:
      return action.converted;
  }
  return state;
}

function sentenceHadExperience(state: boolean = true, action: ImpactActions): boolean {
  switch (action.type) {
    // if a new detail view is selected then reset the filter to the default state
    case ImpactTypes.SetOpportunityDetail:
      return true;
    case ImpactTypes.SetUserSentenceHadExperience:
      return action.hadExperience;
  }
  return state;
}

/** All available custom queries. */
function allCustomQueries(state: CustomQueryProto[] = [], _: ImpactActions): CustomQueryProto[] {
  // TODO wat
  return state;
}

/** Boolean that controls if we show the save config modal */
function showConfigSaveModal(state: boolean = false, action: ImpactConfigActions): boolean {
  switch (action.type) {
    case ImpactTypes.ConfigShowModal:
      return action.show;
  }
  return state;
}

/** Array of configIds */
function configIds(state: int64[] = [], action: ImpactConfigActions): int64[] {
  switch (action.type) {
    case ImpactTypes.FetchImpactsConfigSuccess:
      return action.data.map(d => d.id);
    case ImpactTypes.ConfigDeleteSuccess:
      return state.filter(d => d !== action.config.id);
    case ImpactTypes.ConfigCreateSuccess:
      return [...state, action.config.id];
  }
  return state;
}

/** Map of saved configured inputs */
function configsById(state: ConfigById = {}, action: ImpactConfigActions): ConfigById {
  switch (action.type) {
    case ImpactTypes.FetchImpactsConfigSuccess: {
      const configsById: ConfigById = {};
      action.data.forEach(d => (configsById[d.id as string] = d));
      return configsById;
    }
    case ImpactTypes.ConfigCreateSuccess:
      return {
        ...state,
        [action.config.id as string]: action.config,
      };
    case ImpactTypes.ConfigUpdateSuccess:
      return {
        ...state,
        [action.config.id as string]: action.config,
      };
    case ImpactTypes.ConfigDeleteSuccess: {
      const { [action.config.id as string]: value, ...configById } = state;
      return configById;
    }
  }
  return state;
}

type ListAndId<T> = {
  list: UID[];
  byId: { [key: string]: T };
};

type GeneratedOpportunitiesState = {
  loading: ReturnType<ReturnType<typeof createLoadingReducer>>;
  error: ReturnType<ReturnType<typeof createErrorReducer>>;
  opportunityList: string[];
  opportunitiesById: { [key: string]: OpportunitiesWithID };
};
const generatedOpportunities = combineReducers<GeneratedOpportunitiesState>({
  loading: createLoadingReducer(
    ImpactTypes.GenerateOpportunitiesRequest,
    ImpactTypes.GenerateOpportunitiesSuccess,
    ImpactTypes.GenerateOpportunitiesFailure,
  ),
  error: createErrorReducer(
    ImpactTypes.GenerateOpportunitiesRequest,
    ImpactTypes.GenerateOpportunitiesSuccess,
    ImpactTypes.GenerateOpportunitiesFailure,
  ),
  // Flat list of opportunity IDs
  opportunityList: function opportunityList(
    state: string[] = [],
    action: ImpactActions | ImpactConfigActions,
  ): string[] {
    switch (action.type) {
      case ImpactTypes.GenerateOpportunityResults:
        // requests come in batches, previously fetched opps must remain in state
        return [...state, ...action.data.opportunities.map(d => d.id)];
      case ImpactTypes.GenerateSignalsRequest:
      case ImpactTypes.ConfigNew:
        return [];
    }
    return state;
  },
  // Map of opportuntiy IDs back to an opportunity, holds the actual reference to the Opportunity.
  opportunitiesById: function opportunitiesById(
    state: { [key: string]: OpportunitiesWithID } = {},
    action: ImpactActions | ImpactConfigActions,
  ): { [key: string]: OpportunitiesWithID } {
    switch (action.type) {
      case ImpactTypes.GenerateOpportunityResults: {
        // requests come in batches, previously fetched opps must remain in state
        const byId = {
          ...state,
        };
        action.data.opportunities.forEach(d => (byId[d.id] = d));
        return byId;
      }
      case ImpactTypes.GenerateSignalsRequest:
      case ImpactTypes.ConfigNew:
        return {};
    }
    return state;
  },
});

function opportunityDetail(state: string = '', action: ImpactActions) {
  switch (action.type) {
    case ImpactTypes.SetOpportunityDetail:
      return action.id;
  }
  return state;
}

type GeneratedSignalsState = {
  loading: ReturnType<ReturnType<typeof createLoadingReducer>>;
  error: ReturnType<ReturnType<typeof createErrorReducer>>;
  list: string[];
  byId: ListAndId<SignalsWithID>['byId'];
};
const generatedSignals = combineReducers<GeneratedSignalsState>({
  loading: createLoadingReducer(
    ImpactTypes.GenerateSignalsRequest,
    ImpactTypes.GenerateSignalsSuccess,
    ImpactTypes.GenerateSignalsFailure,
  ),
  error: createErrorReducer(
    ImpactTypes.GenerateSignalsRequest,
    ImpactTypes.GenerateSignalsSuccess,
    ImpactTypes.GenerateSignalsFailure,
  ),
  list(state: string[] = [], action: ImpactActions | ImpactConfigActions): string[] {
    switch (action.type) {
      case ImpactTypes.GenerateSignalsSuccess:
        return action.data.signals.map(d => d.id);
      case ImpactTypes.GenerateSignalsRequest:
      case ImpactTypes.ConfigNew:
        return [];
    }
    return state;
  },
  byId(
    state: ListAndId<SignalsWithID>['byId'] = {},
    action: ImpactActions | ImpactConfigActions,
  ): ListAndId<SignalsWithID>['byId'] {
    switch (action.type) {
      case ImpactTypes.GenerateSignalsSuccess: {
        const byId: ListAndId<SignalsWithID>['byId'] = {};
        action.data.signals.forEach(signal => (byId[signal.id] = signal));
        return byId;
      }
      case ImpactTypes.GenerateSignalsRequest:
      case ImpactTypes.ConfigNew:
        return {};
    }
    return state;
  },
});

function generateOpportunitiesStatus(
  state: OpportunitiesLoadingStatus = OpportunitiesLoadingStatus.reset,
  action: ImpactActions | ImpactConfigActions,
): OpportunitiesLoadingStatus {
  switch (action.type) {
    case ImpactTypes.ConfigNew:
      return OpportunitiesLoadingStatus.reset;
    case ImpactTypes.GenerateSignalsRequest:
      return OpportunitiesLoadingStatus.fetchingSignals;
    case ImpactTypes.GenerateOpportunitiesBatchStarted:
      return OpportunitiesLoadingStatus.fetchingOpportunities;
    case ImpactTypes.GenerateOpportunitiesBatchSuccess:
      return OpportunitiesLoadingStatus.done;
    case ImpactTypes.GenerateOpportunitiesBatchFailure:
    case ImpactTypes.GenerateSignalsFailure:
      return OpportunitiesLoadingStatus.failed;
    default:
      return state;
  }
}

function opportunityBatchVersion(state: string = '', action: ImpactActions): string {
  switch (action.type) {
    case ImpactTypes.SetOpportunityBatchVersion:
      return action.opportunityBatchVersion;
  }
  return state;
}

function generateOpportunitiesProgress(
  state: GenerateOpportunitiesProgress = { completed: 0, total: 0 },
  action: ImpactActions,
): GenerateOpportunitiesProgress {
  switch (action.type) {
    case ImpactTypes.UpdateGenerateOpportunitiesProgress:
      return action.progress;
  }
  return state;
}

const funnelMetricsRequestReducer = createRequestReducer<ComputeFunnelMetricsResponse>()(
  ImpactTypes.ComputeFunnelMetricsRequest,
  ImpactTypes.ComputeFunnelMetricsSuccess,
  ImpactTypes.ComputeFunnelMetricsFailure,
);

const defaultFunnelMetricsState: RequestState<ComputeFunnelMetricsResponse> = {
  lastRequested: -1,
  lastResponded: -1,
  loading: false,
  error: false,
  data: { data: { steps: [] }, computedAt: null, backgroundRefId: '', sampleResults: null },
  safeMessage: null,
};

type funnelMetricsAction =
  | ActionType<typeof ImpactConfigActions.configNew>
  | DispatchedAction<
      ImpactTypes.ComputeFunnelMetricsRequest,
      ImpactTypes.ComputeFunnelMetricsSuccess,
      ImpactTypes.ComputeFunnelMetricsFailure,
      ComputeFunnelMetricsRequest,
      ComputeFunnelMetricsResponse
    >;

function funnelMetrics(
  state: RequestState<ComputeFunnelMetricsResponse> = defaultFunnelMetricsState,
  action: funnelMetricsAction,
): RequestState<ComputeFunnelMetricsResponse> {
  switch (action.type) {
    case ImpactTypes.ConfigNew:
      return {
        ...state,
        data: defaultFunnelMetricsState.data,
      };
    default:
      return funnelMetricsRequestReducer(state, action);
  }
}

type OpportunitySnapshotsState = {
  loading: ReturnType<ReturnType<typeof createLoadingReducer>>;
  error: ReturnType<ReturnType<typeof createErrorReducer>>;
  byId: ListAndId<OpportunitySnapshot>['byId'];
};
const opportunitySnapshots = combineReducers<OpportunitySnapshotsState>({
  loading: createLoadingReducer(
    ImpactTypes.FetchOpportunitySnapshotsRequest,
    ImpactTypes.FetchOpportunitySnapshotsSuccess,
    ImpactTypes.FetchOpportunitySnapshotsFailure,
  ),
  error: createErrorReducer(
    ImpactTypes.FetchOpportunitySnapshotsRequest,
    ImpactTypes.FetchOpportunitySnapshotsSuccess,
    ImpactTypes.FetchOpportunitySnapshotsFailure,
  ),
  byId(
    state: ListAndId<OpportunitySnapshot>['byId'] = {},
    action: ImpactFindAndFixActions,
  ): ListAndId<OpportunitySnapshot>['byId'] {
    switch (action.type) {
      case ImpactTypes.FetchOpportunitySnapshotsSuccess: {
        const byId: ListAndId<OpportunitySnapshot>['byId'] = {};
        action.data.forEach(snapshot => (byId[snapshot.id] = snapshot));
        return byId;
      }
      case ImpactTypes.CreateOpportunitySnapshotSuccess:
        return {
          ...state,
          [action.data.id]: action.data,
        };
      case ImpactTypes.FetchOpportunitySnapshotsRequest:
        return {};
      case ImpactTypes.FetchOpportunitySnapshotSuccess: {
        const stateById = clone(state);
        stateById[action.data.id] = action.data;
        return stateById;
      }
      case ImpactTypes.DeleteOpportunitySnapshotSuccess: {
        const nState = clone(state);
        delete nState[action.data];
        return nState;
      }
    }
    return state;
  },
});

type OpportunitySnapshotState = {
  loading: ReturnType<ReturnType<typeof createLoadingReducer>>;
  error: ReturnType<ReturnType<typeof createErrorReducer>>;
  data: OpportunitySnapshot | null;
};
const opportunitySnapshot = combineReducers<OpportunitySnapshotState>({
  loading: createLoadingReducer(
    ImpactTypes.FetchOpportunitySnapshotRequest,
    ImpactTypes.FetchOpportunitySnapshotSuccess,
    ImpactTypes.FetchOpportunitySnapshotFailure,
  ),
  error: createErrorReducer(
    ImpactTypes.FetchOpportunitySnapshotRequest,
    ImpactTypes.FetchOpportunitySnapshotSuccess,
    ImpactTypes.FetchOpportunitySnapshotFailure,
  ),
  data(
    state: OpportunitySnapshot | null = null,
    action: ImpactFindAndFixActions,
  ): OpportunitySnapshot | null {
    switch (action.type) {
      case ImpactTypes.FetchOpportunitySnapshotSuccess:
        return action.data;
      case ImpactTypes.FetchOpportunitySnapshotRequest:
      case ImpactTypes.DeleteOpportunitySnapshotSuccess:
        return null;
    }
    return state;
  },
});

function snapshotId(state: string | null = null, action: ImpactFindAndFixActions) {
  switch (action.type) {
    case ImpactTypes.SetSnapshotId:
      return action.snapshotId;
  }
  return state;
}

type SnapshotShareLinkState = {
  loading: ReturnType<ReturnType<typeof createLoadingReducer>>;
  error: ReturnType<ReturnType<typeof createErrorReducer>>;
  data: string | null;
};
const snapshotShareLink = combineReducers<SnapshotShareLinkState>({
  loading: createLoadingReducer(
    ImpactTypes.CreateOpportunitySnapshotShareLinkRequest,
    ImpactTypes.CreateOpportunitySnapshotShareLinkSuccess,
    ImpactTypes.CreateOpportunitySnapshotShareLinkFailure,
  ),
  error: createErrorReducer(
    ImpactTypes.CreateOpportunitySnapshotShareLinkRequest,
    ImpactTypes.CreateOpportunitySnapshotShareLinkSuccess,
    ImpactTypes.CreateOpportunitySnapshotShareLinkFailure,
  ),
  data(state: string | null = null, action: ImpactFindAndFixActions): string | null {
    switch (action.type) {
      case ImpactTypes.CreateOpportunitySnapshotShareLinkRequest:
      case ImpactTypes.CreateOpportunitySnapshotShareLinkFailure:
      case ImpactTypes.FetchOpportunitySnapshotRequest:
        return null;
      case ImpactTypes.CreateOpportunitySnapshotShareLinkSuccess:
        return action.data.shareLinkPath;
    }
    return state;
  },
});

function shareLinkPopoverToggle(state: boolean = false, action: ImpactFindAndFixActions) {
  switch (action.type) {
    case ImpactTypes.SetShareLinkPopover:
      return action.open;
    case ImpactTypes.FetchOpportunitySnapshotRequest:
      return false;
  }
  return state;
}

function deleteSnapshotPending(state: boolean = false, action: ImpactFindAndFixActions) {
  switch (action.type) {
    case ImpactTypes.SetDeleteSnapshotPending:
      return action.pending;
  }
  return state;
}

function pendingSnapshot(state: string | null = null, action: ImpactFindAndFixActions) {
  switch (action.type) {
    case ImpactTypes.SetDeleteSnapshotPending:
      return action.snapshotId;
  }
  return state;
}

type SnapshotShareLinksState = {
  loading: ReturnType<ReturnType<typeof createLoadingReducer>>;
  error: ReturnType<ReturnType<typeof createErrorReducer>>;
  data: SnapshotShareLinkResponse[];
};
const snapshotShareLinks = combineReducers<SnapshotShareLinksState>({
  loading: createLoadingReducer(
    ImpactTypes.GetOpportunitySnapshotShareLinksRequest,
    ImpactTypes.GetOpportunitySnapshotShareLinksSuccess,
    ImpactTypes.GetOpportunitySnapshotShareLinksFailure,
  ),
  error: createErrorReducer(
    ImpactTypes.GetOpportunitySnapshotShareLinksRequest,
    ImpactTypes.GetOpportunitySnapshotShareLinksSuccess,
    ImpactTypes.GetOpportunitySnapshotShareLinksFailure,
  ),
  data(
    state: SnapshotShareLinkResponse[] = [],
    action: ImpactFindAndFixActions,
  ): SnapshotShareLinkResponse[] {
    switch (action.type) {
      case ImpactTypes.GetOpportunitySnapshotShareLinksRequest:
      case ImpactTypes.GetOpportunitySnapshotShareLinksFailure:
        return [];
      case ImpactTypes.GetOpportunitySnapshotShareLinksSuccess:
        return action.data;
    }
    return state;
  },
});

type MutedOpportunityConfigsState = {
  loading: ReturnType<ReturnType<typeof createLoadingReducer>>;
  error: ReturnType<ReturnType<typeof createErrorReducer>>;
  bySignalKey: MutedOppConfigsBySignalKey;
};
const mutedOpportunityConfigs = combineReducers<MutedOpportunityConfigsState>({
  loading: createLoadingReducer(
    ImpactTypes.GetMutedOpportunityConfigsRequest,
    ImpactTypes.GetMutedOpportunityConfigsSuccess,
    ImpactTypes.GetMutedOpportunityConfigsFailure,
  ),
  error: createErrorReducer(
    ImpactTypes.GetMutedOpportunityConfigsRequest,
    ImpactTypes.GetMutedOpportunityConfigsSuccess,
    ImpactTypes.GetMutedOpportunityConfigsFailure,
  ),
  bySignalKey(
    state: MutedOppConfigsBySignalKey = {},
    action: ImpactActions,
  ): MutedOppConfigsBySignalKey {
    switch (action.type) {
      case ImpactTypes.GetMutedOpportunityConfigsSuccess:
        return action.data.reduce(
          (
            acc: MutedOppConfigsBySignalKey,
            val: MutedOpportunityConfig,
          ): MutedOppConfigsBySignalKey => ({
            ...acc,
            [val.signalKey]: val,
          }),
          {},
        );
      case ImpactTypes.MuteOpportunitySuccess:
        return action.data?.signalKey ? { ...state, [action.data.signalKey]: action.data } : state;
      case ImpactTypes.UnmuteOpportunitySuccess:
        return Object.keys(state).reduce(
          (acc: MutedOppConfigsBySignalKey, signalKey: string): MutedOppConfigsBySignalKey => {
            const config = state[signalKey];
            if (config && (signalKey !== action.request?.body?.signal?.signalKey ?? '')) {
              acc[signalKey] = config;
            }
            return acc;
          },
          {},
        );
      default:
        return state;
    }
  },
});

/** The status of the request to fetch impact configs. */
const fetchImpactsConfigStatus = fetchAction({
  loading: ImpactTypes.FetchImpactsConfigRequest,
  failed: ImpactTypes.FetchImpactsConfigFailed,
  loaded: ImpactTypes.FetchImpactsConfigSuccess,
});

type ImpactReducerState = {
  configIds: ReturnType<typeof configIds>;
  configsById: ReturnType<typeof configsById>;
  currentConfigId: ReturnType<typeof currentConfigId>;
  currentConfig: ReturnType<typeof currentConfig>;
  pendingConfig: ReturnType<typeof pendingConfig>;
  allCustomQueries: ReturnType<typeof allCustomQueries>;
  opportunityDetail: ReturnType<typeof opportunityDetail>;
  fetchImpactsConfigStatus: ReturnType<typeof fetchImpactsConfigStatus>;
  showConfigSaveModal: ReturnType<typeof showConfigSaveModal>;
  detailPaginationStep: ReturnType<typeof detailPaginationStep>;
  userSentence: ReturnType<typeof userSentence>;
  funnelMetrics: ReturnType<typeof funnelMetrics>;
  generatedOpportunities: ReturnType<typeof generatedOpportunities>;
  generatedSignals: ReturnType<typeof generatedSignals>;
  generateOpportunitiesStatus: ReturnType<typeof generateOpportunitiesStatus>;
  opportunityBatchVersion: ReturnType<typeof opportunityBatchVersion>;
  opportunitySnapshots: ReturnType<typeof opportunitySnapshots>;
  snapshotId: ReturnType<typeof snapshotId>;
  opportunitySnapshot: ReturnType<typeof opportunitySnapshot>;
  snapshotShareLink: ReturnType<typeof snapshotShareLink>;
  shareLinkPopoverToggle: ReturnType<typeof shareLinkPopoverToggle>;
  dimensions: ReturnType<typeof dimensions>;
  deleteSnapshotPending: ReturnType<typeof deleteSnapshotPending>;
  snapshotShareLinks: ReturnType<typeof snapshotShareLinks>;
  pendingSnapshot: ReturnType<typeof pendingSnapshot>;
  revenueAttribution: ReturnType<typeof revenueAttribution>;
  mutedOpportunityConfigs: ReturnType<typeof mutedOpportunityConfigs>;
  generateOpportunitiesProgress: ReturnType<typeof generateOpportunitiesProgress>;
  conversionsSpec: ReturnType<typeof conversionsSpec>;
};

export const impactReducer = combineReducers<ImpactReducerState>({
  configIds,
  configsById,
  currentConfigId,
  currentConfig,
  pendingConfig,
  allCustomQueries,
  opportunityDetail,
  fetchImpactsConfigStatus,
  showConfigSaveModal,
  detailPaginationStep,
  userSentence,
  funnelMetrics,
  generatedOpportunities,
  generatedSignals,
  generateOpportunitiesStatus,
  opportunityBatchVersion,
  opportunitySnapshots,
  snapshotId,
  opportunitySnapshot,
  snapshotShareLink,
  shareLinkPopoverToggle,
  dimensions,
  deleteSnapshotPending,
  snapshotShareLinks,
  pendingSnapshot,
  revenueAttribution,
  mutedOpportunityConfigs,
  generateOpportunitiesProgress,
  conversionsSpec,
});
